import "./Dropdown.scss";
import { FunctionComponent } from "react";
import Select, { components, MultiValueGenericProps, MultiValueProps } from "react-select";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface IProps {
  label?: string;
  options: any;
  placeholder: string;
  value?: any;
  defaultValue?: any;
  onChange: any;
  className?: string;
  isMulti?: boolean;
  innerRef?: any;
  onFocus?: any;
  onInputChange?: any;
  infoIcon?: boolean;
  infoMessage?: string;
}

const SortableMultiValue = (props: MultiValueProps<any>) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.data.value });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <components.MultiValue {...props} />
    </div>
  );
};

const SortableMultiValueLabel = (props: MultiValueGenericProps) => {
  const { attributes, listeners, setNodeRef } = useSortable({ id: props.data.value });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}>
      <components.MultiValueLabel {...props} />
    </div>
  );
};

const Dropdown: FunctionComponent<IProps> = (props) => {
  const {
    defaultValue,
    label,
    onChange,
    options,
    placeholder,
    isMulti,
    className,
    value,
    innerRef,
    onFocus,
    onInputChange,
    infoIcon, 
    infoMessage
  } = props;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = value.findIndex((item: any) => item.value === active.id);
      const newIndex = value.findIndex((item: any) => item.value === over?.id);
      const newValue = arrayMove(value, oldIndex, newIndex);
      onChange(newValue);
    }
  };

  return (
    <div className="dropdown-container">
      <div className="label-info">
        {label && <label>{label}</label>}
        {infoIcon && 
          <span className="info-icon">
            <span className="icon-question"></span>
            <span className="tooltip">{infoMessage}</span>
          </span>
        }
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value.map((item: any) => item.value)} strategy={verticalListSortingStrategy}>
          <Select
            ref={innerRef}
            className={"basic-single " + className}
            classNamePrefix="select"
            placeholder={placeholder}
            isMulti={isMulti}
            isDisabled={false}
            isLoading={false}
            isClearable={false}
            isRtl={false}
            isSearchable={true}
            options={options}
            value={value}
            onChange={onChange}
            defaultValue={defaultValue}
            onFocus={onFocus}
            onInputChange={onInputChange}
            components={{
              MultiValue: SortableMultiValue,
              MultiValueLabel: SortableMultiValueLabel,
            }}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default Dropdown;
